import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  unitsApi,
  UNIT_INSTRUCTIONS_APPLY_KEY,
  UNIT_INSTRUCTION_BULK_APPLY_KEY
} from "api/units";
import type { InstructionBulkApply } from "types/landlord/address";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  getApplyErrorMessage,
  isSettledInstructionStatus,
  unitLabel
} from "shared/app/ApplyUnitInstructions/helpers";

const MAX_CONSECUTIVE_POLL_ERRORS = 10;
const MAX_POLL_DURATION_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 1000;

type BulkUnitInstructionsRun = {
  sourceUnitId: number;
  bulkApplyId: number;
  startedAt: number;
};

const STORAGE_KEY = "bulkUnitInstructionsProgress.run";

const readStoredRun = (): BulkUnitInstructionsRun | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const isValid =
      typeof parsed?.sourceUnitId === "number" &&
      typeof parsed?.bulkApplyId === "number" &&
      typeof parsed?.startedAt === "number";
    return isValid ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredRun = (run: BulkUnitInstructionsRun | null) => {
  try {
    if (run) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
};

type BulkUnitInstructionsProgressContext = {
  run: BulkUnitInstructionsRun | null;
  status: InstructionBulkApply | undefined;
  isStatusError: boolean;
  hasGivenUpPolling: boolean;
  isCancelling: boolean;
  hasRequestedCancel: boolean;
  startTracking: (run: Omit<BulkUnitInstructionsRun, "startedAt">) => void;
  cancelRun: () => void;
  dismiss: () => void;
};

const BulkUnitInstructionsProgressContext =
  createContext<BulkUnitInstructionsProgressContext>({
    run: null,
    status: undefined,
    isStatusError: false,
    hasGivenUpPolling: false,
    isCancelling: false,
    hasRequestedCancel: false,
    startTracking: () => {},
    cancelRun: () => {},
    dismiss: () => {}
  });

export function BulkUnitInstructionsProgressProvider({
  children
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const dictionary = useUnitsPropertiesDictionary();
  const [run, setRun] = useState<BulkUnitInstructionsRun | null>(readStoredRun);
  const settledRunIdRef = useRef<number | null>(null);
  const hasObservedUnsettledRef = useRef(false);
  const [hasRequestedCancel, setHasRequestedCancel] = useState(false);

  const statusQueryKey = [
    UNIT_INSTRUCTION_BULK_APPLY_KEY,
    run?.sourceUnitId,
    run?.bulkApplyId
  ];

  const { data: status, isError: isStatusError } =
    useQuery<InstructionBulkApply>({
      queryKey: statusQueryKey,
      queryFn: () =>
        unitsApi.getInstructionBulkApply(run!.sourceUnitId, run!.bulkApplyId),
      enabled: run !== null,
      refetchInterval: (query) => {
        const currentStatus = query.state.data?.status;
        if (currentStatus && isSettledInstructionStatus(currentStatus))
          return false;
        if (query.state.fetchFailureCount >= MAX_CONSECUTIVE_POLL_ERRORS)
          return false;
        if (run && Date.now() - run.startedAt > MAX_POLL_DURATION_MS)
          return false;

        return POLL_INTERVAL_MS;
      }
    });

  const hasGivenUpPolling = Boolean(
    run &&
    status &&
    !isSettledInstructionStatus(status.status) &&
    Date.now() - run.startedAt > MAX_POLL_DURATION_MS
  );

  const { mutate: cancelRun, isPending: isCancelling } = useMutation({
    mutationKey: [UNIT_INSTRUCTIONS_APPLY_KEY, "CANCEL", "UPDATE"],
    mutationFn: () =>
      unitsApi.cancelInstructionBulkApply(run!.sourceUnitId, run!.bulkApplyId),
    onSuccess: () => {
      setHasRequestedCancel(true);
      queryClient.invalidateQueries({ queryKey: statusQueryKey });
    },
    onError: (mutationError) =>
      toast.error(getApplyErrorMessage(mutationError, dictionary))
  });

  useEffect(() => {
    if (!status || !run) return;
    if (!isSettledInstructionStatus(status.status)) {
      hasObservedUnsettledRef.current = true;
      return;
    }
    if (settledRunIdRef.current === status.id) return;

    settledRunIdRef.current = status.id;
    queryClient.invalidateQueries({ queryKey: ["unit"] });
    queryClient.invalidateQueries({ queryKey: ["units"] });
    queryClient.invalidateQueries({ queryKey: ["properties"] });

    if (!hasObservedUnsettledRef.current) return;

    const label = unitLabel(status.updated_count, dictionary);
    if (status.status === "cancelled")
      toast.info(
        `Apply instructions cancelled — ${status.updated_count} ${label} already updated were kept`
      );
    else if (status.status === "completed" && status.failed.length === 0)
      toast.success(`Instructions applied to ${status.updated_count} ${label}`);
  }, [status, run, queryClient, dictionary]);

  const startTracking = useCallback(
    (next: Omit<BulkUnitInstructionsRun, "startedAt">) => {
      const withStartedAt = { ...next, startedAt: Date.now() };
      settledRunIdRef.current = null;
      hasObservedUnsettledRef.current = true;
      setHasRequestedCancel(false);
      writeStoredRun(withStartedAt);
      setRun(withStartedAt);
    },
    []
  );

  const dismiss = useCallback(() => {
    writeStoredRun(null);
    setRun(null);
  }, []);

  const value = useMemo(
    () => ({
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    }),
    [
      run,
      status,
      isStatusError,
      hasGivenUpPolling,
      isCancelling,
      hasRequestedCancel,
      startTracking,
      cancelRun,
      dismiss
    ]
  );

  return (
    <BulkUnitInstructionsProgressContext.Provider value={value}>
      {children}
    </BulkUnitInstructionsProgressContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkUnitInstructionsProgress() {
  return useContext(BulkUnitInstructionsProgressContext);
}
